import { useCallback, useEffect, useMemo, useState } from "react";
import { tr } from "../../i18n/tr";
import { PenLine, CheckCircle2, UserCheck, Users, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "../SignaturePad";
import Modal from "./Modal";

const SLOTS = [{ k: "matin", l: tr("lms.matin_163c", "Matin") }, { k: "apres-midi", l: tr("lms.apres_midi_e026", "Après-midi") }, { k: "journee", l: tr("lms.journee_66c8", "Journée") }];
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Émargement par signature (fonctionne hors ligne via l'API offline : GET en cache, POST en file d'attente).
 * mode "kiosk" : tablette du formateur, les stagiaires signent à tour de rôle (+ signature formateur).
 * mode "self"  : espace stagiaire, l'élève signe sa propre présence.
 */
export function AttendanceKiosk({ api, sessionId, enrollments = [], mode = "kiosk", ownEnrollmentId = null, trainerName = "" }) {
  const [date, setDate] = useState(today());
  const [slot, setSlot] = useState("matin");
  const [sigs, setSigs] = useState([]);
  const [signing, setSigning] = useState(null);
  const listUrl = `/lms/sessions/${sessionId}/attendance/signatures`;

  const load = useCallback(async () => {
    try {
      const r = await api.get(listUrl);
      setSigs(r.data || []);
    } catch (e) { if (!e.offline) toast.error(e.response?.data?.detail || e.message); }
  // eslint-disable-next-line
  }, [sessionId]);
  useEffect(() => { load(); }, [load]);

  const signedSet = useMemo(() => new Set(sigs.filter((s) => s.date === date && s.slot === slot).map((s) => s.enrollment_id)), [sigs, date, slot]);

  const confirm = async ({ signature_b64, signed_name, consent }) => {
    const target = signing;
    try {
      const body = { enrollment_id: target.enrollment_id, date, slot, signature_b64, signed_name, consent, signed_at_client: new Date().toISOString() };
      const r = await api.post(`/lms/sessions/${sessionId}/attendance/sign`, body);
      const rec = r.data?.signature || { ...body, pending: true };
      setSigs((cur) => [...cur.filter((s) => !(s.enrollment_id === rec.enrollment_id && s.date === date && s.slot === slot)), { ...rec, has_signature: true, pending: !!r.queued }]);
      api.patchCache?.(listUrl, (list) => [...(list || []).filter((s) => !(s.enrollment_id === rec.enrollment_id && s.date === date && s.slot === slot)), { ...rec, has_signature: true }]);
      toast.success(r.queued ? "Signature enregistrée — sera synchronisée au retour du réseau" : tr("lms.presence_signee_414d", "Présence signée"));
      setSigning(null);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const rows = mode === "self"
    ? enrollments.filter((e) => e.enrollment_id === ownEnrollmentId)
    : enrollments;
  const presentCount = rows.filter((e) => signedSet.has(e.enrollment_id)).length;

  return (
    <div className="space-y-4" data-testid="attendance-kiosk">
      <div className="flex flex-wrap items-center gap-3 bg-[#14101c] border border-white/10 rounded-2xl p-3">
        <label className="inline-flex items-center gap-2 text-xs text-slate-300"><CalendarDays className="w-4 h-4 text-slate-500" />
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-[#0b0811] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" data-testid="attendance-date" />
        </label>
        <div className="inline-flex rounded-lg overflow-hidden border border-white/10" data-testid="attendance-slots">
          {SLOTS.map((s) => (
            <button key={s.k} onClick={() => setSlot(s.k)} className={"px-3 py-1.5 text-xs font-bold " + (slot === s.k ? "text-[#14101c]" : "text-slate-300 hover:bg-white/5")} style={slot === s.k ? { background: "var(--brand-primary, #f7c935)" } : {}} data-testid={"attendance-slot-" + s.k}>{s.l}</button>
          ))}
        </div>
        {mode === "kiosk" && <span className="ml-auto text-xs text-slate-400 inline-flex items-center gap-1" data-testid="attendance-count"><Users className="w-3.5 h-3.5" /> {presentCount}/{rows.length} présent{presentCount > 1 ? "s" : ""}</span>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {mode === "kiosk" && (
          <Row label={trainerName || "Formateur"} sub="Signature du formateur" signed={signedSet.has("trainer")} pending={sigs.some((s) => s.enrollment_id === "trainer" && s.date === date && s.slot === slot && s.pending)} onSign={() => setSigning({ enrollment_id: "trainer", name: trainerName })} testId="attendance-row-trainer" accent />
        )}
        {rows.map((e) => (
          <Row key={e.enrollment_id} label={e.student_name || e.student_email} sub={e.student_email} signed={signedSet.has(e.enrollment_id)} pending={sigs.some((s) => s.enrollment_id === e.enrollment_id && s.date === date && s.slot === slot && s.pending)} onSign={() => setSigning({ enrollment_id: e.enrollment_id, name: e.student_name })} testId={"attendance-row-" + e.enrollment_id} />
        ))}
        {rows.length === 0 && <div className="text-xs text-slate-500 italic p-4">{tr("lms.aucun_stagiaire_inscrit_c174", "Aucun stagiaire inscrit.")}</div>}
      </div>

      {signing && (
        <Modal onClose={() => setSigning(null)} title={`Signature — ${signing.name || "Formateur"} · ${date} · ${SLOTS.find((s) => s.k === slot)?.l}`}>
          <SignaturePad onConfirm={confirm} onCancel={() => setSigning(null)} defaultName={signing.name || ""} confirmLabel="Valider ma présence" hint={tr("lms.signature_horodatee_conservee_pour_3d02", "Signature horodatée, conservée pour la feuille d'émargement Qualiopi. Fonctionne hors ligne.")} />
        </Modal>
      )}
    </div>
  );
}

function Row({ label, sub, signed, pending, onSign, testId, accent }) {
  return (
    <div className={"flex items-center gap-3 rounded-xl border p-3 " + (signed ? "bg-emerald-500/5 border-emerald-500/20" : "bg-[#14101c] border-white/10")} data-testid={testId}>
      <div className={"w-9 h-9 rounded-full flex items-center justify-center shrink-0 " + (signed ? "bg-emerald-500/20 text-emerald-400" : "bg-white/5 text-slate-500")}>
        {signed ? <CheckCircle2 className="w-5 h-5" /> : accent ? <UserCheck className="w-5 h-5" /> : <PenLine className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-white truncate">{label}</div>
        <div className="text-[10px] text-slate-500 truncate">{signed ? (pending ? "Signé — en attente de synchro" : "Présence signée") : sub}</div>
      </div>
      <button onClick={onSign} className={"px-3 py-1.5 rounded-lg text-xs font-bold " + (signed ? "bg-white/5 text-slate-300 hover:bg-white/10" : "text-[#14101c]")} style={signed ? {} : { background: "var(--brand-primary, #f7c935)" }} data-testid={testId + "-sign"}>
        {signed ? "Re-signer" : tr("lms.signer_d197", "Signer")}
      </button>
    </div>
  );
}

export default AttendanceKiosk;
